import React, { useEffect, useState } from "react";
import PageHeading from "../utils/PageHeading";
import {
  Container,
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  Button,
  styled,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getAllDrugs, findDrugByName as fetchDrugByName } from "../services/drugDataService";
import { convertDosageUnit } from "../utils/function";

import { Link, useLocation } from "react-router-dom";

// Helper to normalize dosage strings for matching across regions
// e.g., "0.5 mg (10 mg/mL)" matches "0.5 mg"
const normalizeDosage = (str) => {
  if (!str) return "";
  let s = str.toLowerCase();

  // Custom logic for Humalog mixes:
  // "Mix 25 (Mix 75/25)" -> extract the inner match and use it
  const mixMatch = s.match(/mix\s*\d+\s*\((mix\s*\d+\/\d+)\)/);
  if (mixMatch) {
     s = mixMatch[1];
  }

  // Treat IU/mL as equivalent to units/mL (e.g. Asia vs USA/Europe insulin dosages)
  s = s.replace(/\biu\b/g, "units");

  return s.replace(/\s*\([^)]*\)/g, "").trim();
};

// Import statements moved to top and previous positions deleted

// ── Animations & Styled Components ──────────────────────────────────────────
const fadeUp = `
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
if (typeof document !== 'undefined') {
  const styleTag = document.getElementById('price-compare-anim') ||
    Object.assign(document.createElement('style'), { id: 'price-compare-anim' });
  styleTag.textContent = fadeUp;
  if (!document.getElementById('price-compare-anim')) document.head.appendChild(styleTag);
}

// USA is always red (highest). Europe/Global use the traffic-light colour from the data.
const accentForUSA = '#e74c3c';   // always red — USA is always highest
const accentForRegion = (color) => {
  if (!color || color === '#f5f5f5') return '#43C5B9';
  if (color === '#8bd699') return '#27ae60';        // green  — lowest range
  if (color === '#ff272766') return '#e74c3c';      // red    — only if data says so
  if (color === '#ffe770b2') return '#F5A623';      // fast gold — mid range
  return '#F5A623'; // fallback to fast gold
};

const ActionButton = styled(Button)({
  backgroundColor: "#43C5B9",
  color: "white",
  borderRadius: "25px",
  textTransform: "none",
  padding: "8px 24px",
  "&:hover": {
    backgroundColor: "#3ab0a5",
  },
});

export default function PriceCompare() {
  const [strength, setStrength] = useState("");
  const [medicineDetails, setMedicineDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dosages, setDosages] = useState([]);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("rxpose_disclaimer_dismissed");
    if (!dismissed) setDisclaimerOpen(true);
  }, []);

  const handleDisclaimerClose = () => {
    sessionStorage.setItem("rxpose_disclaimer_dismissed", "1");
    setDisclaimerOpen(false);
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const medicineName = queryParams.get("medicine")?.toLowerCase();

  // Loaded drug object (replaces synchronous newMedicineData lookups)
  const [loadedDrug, setLoadedDrug] = useState(null);

  // Calculate average prices and process data — now async
  const processData = async () => {
    try {
      setLoading(true);
      setError(null);

      const drugData = await fetchDrugByName(medicineName);

      if (!medicineName || !drugData) {
        setError(`Medicine "${medicineName}" not found in our database.`);
        setLoading(false);
        return;
      }

      setLoadedDrug(drugData);

      // Get all available dosages from ALL regions
      const availableDosages = new Set();

      drugData?.regions?.forEach(region => {
        region?.pharmacies?.forEach(pharmacy => {
          pharmacy?.dosages?.forEach(dosage => {
            if (dosage?.dosage_strength) {
               const s = normalizeDosage(dosage.dosage_strength);
               const f = dosage.form ? dosage.form.trim() : "";
               const combinedStr = f ? `${s} \u2014 ${f}` : s;
               availableDosages.add(combinedStr);
            }
          });
        });
      });

      const sortedDosages = Array.from(availableDosages).sort((a, b) => {
        const getNumericValue = (str) => {
          const match = str?.match(/([\d,]+(?:\.\d+)?)/);
          return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
        };
        return getNumericValue(a) - getNumericValue(b);
      });

      setDosages(sortedDosages);

      // Set default strength if available
      if (sortedDosages?.length > 0 && (!strength || !sortedDosages.includes(strength))) {
        setStrength(sortedDosages[0]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Failed to process data:", err);
      setError("Failed to process medicine data. Please try again.");
      setLoading(false);
    }
  };

  // Calculate the average CASH price for a region for the selected dosage
  const calculateAveragePriceForRegion = (drugData, regionName, selectedStrength) => {
    if (!drugData || !selectedStrength) return 0;

    let totalPrice = 0;
    let count = 0;

    const region = drugData.regions.find(r =>
      r.region_name.toLowerCase().includes(regionName.toLowerCase())
    );

    if (!region) return 0;

    region?.pharmacies?.forEach(pharmacy => {
      pharmacy?.dosages?.forEach(dosage => {
        const d_s = normalizeDosage(dosage.dosage_strength);
        const d_f = dosage.form ? dosage.form.trim() : "";
        const d_combined = d_f ? `${d_s} \u2014 ${d_f}` : d_s;

        if (d_combined === selectedStrength) {
          const price = dosage.price_cash_usd;
          if (price !== undefined && price !== null) {
            totalPrice += parseFloat(price);
            count++;
          }
        }
      });
    });

    return count > 0 ? totalPrice / count : 0;
  };

  // Calculate the average COUPON price for a region for the selected dosage
  const calculateCouponPriceForRegion = (drugData, regionName, selectedStrength) => {
    if (!drugData || !selectedStrength) return 0;

    let totalPrice = 0;
    let count = 0;

    const region = drugData.regions.find(r =>
      r.region_name.toLowerCase().includes(regionName.toLowerCase())
    );

    if (!region) return 0;

    region?.pharmacies?.forEach(pharmacy => {
      pharmacy?.dosages?.forEach(dosage => {
        const d_s = normalizeDosage(dosage.dosage_strength);
        const d_f = dosage.form ? dosage.form.trim() : "";
        const d_combined = d_f ? `${d_s} \u2014 ${d_f}` : d_s;

        if (d_combined === selectedStrength) {
          const price = dosage.price_with_coupon_usd;
          if (price !== undefined && price !== null) {
            totalPrice += parseFloat(price);
            count++;
          }
        }
      });
    });

    return count > 0 ? totalPrice / count : 0;
  };

  // Helper to find the selling unit for the selected dosage
  const findSellingUnit = (drugData, selectedStrength) => {
    if (!drugData || !selectedStrength) return "";
    let unit = "";
    drugData.regions?.forEach(region => {
      region.pharmacies?.forEach(pharmacy => {
        pharmacy.dosages?.forEach(dosage => {
          const d_s = normalizeDosage(dosage.dosage_strength);
          const d_f = dosage.form ? dosage.form.trim() : "";
          const d_combined = d_f ? `${d_s} \u2014 ${d_f}` : d_s;

          if (d_combined === selectedStrength) {
            if (dosage.selling_unit) {
              unit = dosage.selling_unit;
            }
          }
        });
      });
    });
    return unit;
  };

  // Helper to assign traffic-light colors based on price ranking
  const buildColorMap = (prices) => {
    const colorMap = { USA: "#f5f5f5", Europe: "#f5f5f5", Asia: "#f5f5f5" };
    const valid = prices.filter(p => p.price > 0);
    valid.sort((a, b) => a.price - b.price);
    if (valid.length === 1) {
      colorMap[valid[0].name] = "#8bd699";
    } else if (valid.length === 2) {
      if (valid[0].price === valid[1].price) {
        colorMap[valid[0].name] = "#8bd699";
        colorMap[valid[1].name] = "#8bd699";
      } else {
        colorMap[valid[0].name] = "#8bd699";
        colorMap[valid[1].name] = "#ff272766";
      }
    } else if (valid.length >= 3) {
      const minP = valid[0].price;
      const maxP = valid[valid.length - 1].price;
      valid.forEach(p => {
        if (p.price === minP) colorMap[p.name] = "#8bd699";
        else if (p.price === maxP) colorMap[p.name] = "#ff272766";
        else colorMap[p.name] = "#ffe770b2";
      });
    }
    return colorMap;
  };

  // Get the average prices for each region
  const getAveragePricesForRegions = () => {
    const drugData = loadedDrug;

    if (!medicineName || !strength || !drugData) {
      return null;
    }

    // Average CASH prices
    const usaCash = calculateAveragePriceForRegion(drugData, "USA", strength);
    const europeCash = calculateAveragePriceForRegion(drugData, "Europe", strength);
    const asiaCash = calculateAveragePriceForRegion(drugData, "Asia", strength);

    // Average COUPON prices
    const usaCoupon = calculateCouponPriceForRegion(drugData, "USA", strength);
    const europeCoupon = calculateCouponPriceForRegion(drugData, "Europe", strength);
    const asiaCoupon = calculateCouponPriceForRegion(drugData, "Asia", strength);

    // multiplier: how many times more expensive is USA vs region (e.g. 5.3×)
    const europeMultiplier = europeCash > 0 ? usaCash / europeCash : 0;
    const asiaMultiplier   = asiaCash   > 0 ? usaCash / asiaCash   : 0;

    // % difference vs USA (cash): positive means USA is higher than region
    const europeSavingsPercent = europeCash > 0 ? ((usaCash - europeCash) / europeCash) * 100 : 0;
    const asiaSavingsPercent   = asiaCash   > 0 ? ((usaCash - asiaCash)   / asiaCash)   * 100 : 0;

    const cashColorMap   = buildColorMap([
      { name: 'USA',    price: usaCash },
      { name: 'Europe', price: europeCash },
      { name: 'Asia',   price: asiaCash },
    ]);
    const couponColorMap = buildColorMap([
      { name: 'USA',    price: usaCoupon },
      { name: 'Europe', price: europeCoupon },
      { name: 'Asia',   price: asiaCoupon },
    ]);

    const fmt = (v) => v > 0 ? v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A";
    const sellingUnit = findSellingUnit(drugData, strength);

    return {
      description: `${drugData.drug_metadata.generic_name} - ${drugData.drug_metadata.drug_class}`,
      brandName: drugData.drug_metadata.brand_name,
      unit: sellingUnit,
      USA: {
        averagePrice: fmt(usaCash),
        couponPrice: fmt(usaCoupon),
        savingsPercentage: "0.00%",
        color: cashColorMap['USA'],
        couponColor: couponColorMap['USA'],
      },
      Europe: {
        averagePrice: fmt(europeCash),
        couponPrice: fmt(europeCoupon),
        savingsPercentage: Math.abs(europeSavingsPercent).toFixed(1) + "%",
        multiplier: europeMultiplier > 0 ? europeMultiplier.toFixed(1) : null,
        usaIsHigher: europeSavingsPercent > 0,
        isCostlier: europeCash > usaCash,
        color: cashColorMap['Europe'],
        couponColor: couponColorMap['Europe'],
      },
      Asia: {
        averagePrice: fmt(asiaCash),
        couponPrice: fmt(asiaCoupon),
        savingsPercentage: Math.abs(asiaSavingsPercent).toFixed(1) + "%",
        multiplier: asiaMultiplier > 0 ? asiaMultiplier.toFixed(1) : null,
        usaIsHigher: asiaSavingsPercent > 0,
        isCostlier: asiaCash > usaCash,
        color: cashColorMap['Asia'],
        couponColor: couponColorMap['Asia'],
      },
    };
  };

  useEffect(() => {
    processData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineName]);

  useEffect(() => {
    if (medicineName && strength && loadedDrug) {
      const details = getAveragePricesForRegions();
      setMedicineDetails(details);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicineName, strength, loadedDrug]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          color: "white",
          textAlign: "center",
          py: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
        }}
      >
        <Typography
          variant="h6"
          color="error"
          gutterBottom
          sx={{ color: "#ff4d4d" }}
        >
          {error}
        </Typography>
        <Button
          variant="contained"
          sx={{
            mt: 2,
            backgroundColor: "#0e71c8",
            color: "white",
            "&:hover": {
              backgroundColor: "#0a5ca3",
            },
          }}
          onClick={() => window.location.reload()}
        >
          RETRY
        </Button>
      </Box>
    );
  }

  const displayMedicineName = medicineName
    ? medicineName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
    : "Medicine";

  return (
    <div className="about_page body_bg">
      {/* ── Data Source Disclaimer Popup ── */}
      <Dialog open={disclaimerOpen} onClose={handleDisclaimerClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: "1.1rem", sm: "1.25rem" }, color: "#1a202c", pb: 0.5 }}>
          ⚠️ About Our Data
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, color: "#2b6cb0", fontSize: "0.78rem", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5 }}>
            Data Source &amp; Disclaimer
          </Typography>
          <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.8, mb: 2 }}>
            All drug prices are sourced from <strong>publicly available pharmacy websites</strong>, published price lists, and peer-reviewed research. Prices are averages collected periodically and may not reflect real-time rates.
          </Typography>
          <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.8, mb: 2 }}>
            <strong>This is a non-profit, informational website.</strong> We do not sell medications, earn commissions, or have any financial relationship with pharmacies. No transactions occur on this site.
          </Typography>
          <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.8, fontSize: "0.78rem", borderTop: "1px solid #e2e8f0", pt: 1.5 }}>
            Importing prescription drugs into the U.S. is generally illegal under federal law. All information is for educational purposes only. Consult your licensed U.S. healthcare provider for medical advice.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={handleDisclaimerClose} sx={{ backgroundColor: "#43C5B9", color: "white", borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 4, "&:hover": { backgroundColor: "#3ab0a5" } }}>
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      <div className="about_banner_main">
        <PageHeading
          title={"medicines"}
          subtitle={"Price Compare"}
          pageTitle="Price Compare"
          disclaimer="This non-profit website is for informational and educational purposes only. It compares U.S. prescription drug prices with those in other countries using publicly available data. We do not sell medications, facilitate purchases, earn commissions, or have any financial ties to pharmacies. No transactions occur on this site. Links to international websites are provided solely to allow users to verify the price data shown and do not endorse buying or importing drugs from foreign sources. Importing prescription drugs into the U.S. is generally illegal under federal law. The FDA cannot ensure the safety of foreign medications. Any importation decision is solely the user's responsibility. This site provides no medical advice. Consult your licensed U.S. healthcare provider. Use of this site is at your own risk."
        />
      </div>
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 2, sm: 3, md: 4 } }}
        className="medicines_page"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            mb: 4,
            gap: { xs: 3, md: 4 },
          }}
        >
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                textTransform: "capitalize",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              }}
              className="heading_three"
            >
              {displayMedicineName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                maxWidth: { xs: "100%", md: "450px" },
                color: "#000000",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {medicineDetails?.description || (
                <>
                  Compare {displayMedicineName} prices available at
                  international online pharmacies with local U.S. pharmacy
                  coupon prices. We are{" "}
                  <b>
                    {" "}
                    showing {convertDosageUnit(strength)}
                    {medicineDetails?.unit ? ` per ${medicineDetails.unit}` : (strength && strength.includes("units/ml") ? "" : " per 100 tablets")}
                  </b>
                  .
                </>
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: { xs: "100%", md: "300px" },
              bgcolor: "#f5f5f5",
              p: { xs: 2, sm: 3 },
              mb: { xs: 3, md: 6 },
              width: { xs: "100%", md: "50%" },
              height: "auto",
            }}
          >
            <Typography variant="subtitle1" gutterBottom className="view-text">
              Viewing
            </Typography>

            <Select
              fullWidth
              value={strength}
              onChange={(e) => setStrength(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                backgroundColor: "#43C5B9",
                "& .MuiSelect-select": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" }
              }}
              className="select-box"
            >
              {dosages.map((dosage, index) => (
                <MenuItem key={`dosage-${index}`} value={dosage}>
                  {convertDosageUnit(dosage)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
             MODERN ANIMATED PRICE COMPARISON SECTION
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{ mb: 4 }}>

          {/* Section label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, background: 'linear-gradient(180deg,#43C5B9,#1F51AB)' }} />
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' }, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#4a5568' }}>
              Cash Price — Without Insurance or Coupon
            </Typography>
          </Box>

          {/* ── CASH PRICE CARDS ── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 2, md: 2.5 },
              mb: 3,
            }}
          >
            {[
              { label: 'USA', title: 'Average U.S. Price', price: medicineDetails?.USA?.averagePrice, badge: null, badgeVs: null, accentColor: accentForUSA },
              { label: 'Europe', title: 'Average European Price', price: medicineDetails?.Europe?.averagePrice, badge: medicineDetails?.Europe?.multiplier ? `USA is ${medicineDetails?.Europe?.multiplier}× higher` : null, badgeVs: 'vs Europe average', badgeDown: medicineDetails?.Europe?.usaIsHigher, accentColor: accentForRegion(medicineDetails?.Europe?.color) },
              { label: 'Global', title: 'Average Global Price', price: medicineDetails?.Asia?.averagePrice, badge: medicineDetails?.Asia?.multiplier ? `USA is ${medicineDetails?.Asia?.multiplier}× higher` : null, badgeVs: 'vs Global average', badgeDown: medicineDetails?.Asia?.usaIsHigher, accentColor: accentForRegion(medicineDetails?.Asia?.color) },
            ].map((card, i) => (
              <Box
                key={card.label}
                sx={{
                  background: card.accentColor === '#e74c3c' ? '#fff5f5' : card.accentColor === '#F5A623' ? '#fffdf0' : card.accentColor === '#27ae60' ? '#f0fdf4' : '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                  borderTop: `4px solid ${card.accentColor}`,
                  p: { xs: 2.5, md: 3 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `fadeUp 0.5s ease ${i * 0.12}s both`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.13)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: '70px',
                    background: `linear-gradient(135deg,${card.accentColor}18,transparent)`,
                    pointerEvents: 'none',
                  },
                }}
              >
                {/* Region pill */}
                <Box sx={{ backgroundColor: `${card.accentColor}22`, borderRadius: '99px', px: 1.5, py: 0.3, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: card.accentColor, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {card.label}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: '0.8rem', color: '#718096', fontWeight: 500, mb: 0.5 }}>
                  {card.title}
                </Typography>

                <Typography sx={{ fontSize: { xs: '2rem', md: '2.4rem' }, fontWeight: 800, color: '#1a202c', lineHeight: 1, letterSpacing: '-0.02em' }}>
                  ${card.price}
                </Typography>

                {(medicineDetails?.unit || strength) && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#a0aec0', mt: 0.2 }}>
                    per {[strength, medicineDetails?.unit].filter(Boolean).join(' · ')}
                  </Typography>
                )}

                {card.badge && (
                  <Box sx={{ mt: 1, backgroundColor: '#fdecea', borderRadius: '10px', px: 1.5, py: 0.7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        backgroundColor: '#e74c3c',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 900, lineHeight: 1 }}>
                          ↑
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#c0392b' }}>
                        {card.badge}
                      </Typography>
                    </Box>
                    {card.badgeVs && (
                      <Typography sx={{ fontSize: '0.65rem', color: '#718096' }}>
                        {card.badgeVs}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* ── USA COUPON SPOTLIGHT ── */}
          {medicineDetails?.USA?.couponPrice && medicineDetails.USA.couponPrice !== 'N/A' && (
            <Box
              sx={{
                background: 'linear-gradient(135deg, #7b1d1d 0%, #b91c1c 55%, #dc2626 100%)',
                borderRadius: '20px',
                p: { xs: 2.5, sm: 3.5 },
                mb: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(185,28,28,0.35)',
                animation: 'fadeUp 0.5s ease 0.35s both',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(185,28,28,0.45)' },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '-40px', right: '-40px',
                  width: '160px', height: '160px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.05)',
                  pointerEvents: 'none',
                },
              }}
            >
              {/* Left content */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                  <Box sx={{ background: 'rgba(255,255,255,0.18)', borderRadius: '99px', px: 1.5, py: 0.2 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff', letterSpacing: '0.1em', textTransform: 'uppercase' }}>🇺🇸 USA Only</Typography>
                  </Box>
                  <Box sx={{ background: 'rgba(255,255,255,0.18)', borderRadius: '99px', px: 1.5, py: 0.2 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fecaca', letterSpacing: '0.1em', textTransform: 'uppercase' }}>With Coupon</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.2rem' }, color: '#fff', mb: 0.4, lineHeight: 1.3 }}>
                  Even with coupons, Americans still pay far more.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.83rem', maxWidth: 420, lineHeight: 1.6 }}>
                  After applying a coupon, US patients still pay significantly more than patients in Europe or the rest of the world — paying full cash price with no coupon.
                </Typography>
              </Box>

              {/* Right price */}
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '16px',
                  px: { xs: 2.5, sm: 4 },
                  py: { xs: 1.5, sm: 2.5 },
                  textAlign: 'center',
                  minWidth: { xs: '100%', sm: '180px' },
                  border: '1px solid rgba(255,255,255,0.25)',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.3 }}>Avg. USA Coupon</Typography>
                <Typography sx={{ fontSize: { xs: '2.2rem', sm: '2.8rem' }, fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: '-0.03em' }}>
                  ${medicineDetails.USA.couponPrice}
                </Typography>
                {medicineDetails?.USA?.averagePrice && medicineDetails.USA.averagePrice !== 'N/A' && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 0.5, textDecoration: 'line-through' }}>
                    Cash: ${medicineDetails.USA.averagePrice}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* CTA */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <Link
              to={`/drug-details?medicine=${encodeURIComponent(medicineName)}&dosage=${encodeURIComponent(strength)}`}
              style={{ textDecoration: 'none' }}
            >
              <ActionButton variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }} className="medi-btn">
                Drug Details Page
              </ActionButton>
            </Link>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 0 } }} className="contact-content">
          <Typography
            variant="body1"
            gutterBottom
            sx={{
              mt: 4,
              mb: 3,
              color: "#122C3A",
              fontSize: { xs: "1.5rem", sm: "1rem", md: "1.8rem" },
              textAlign: "left",
              paddingLeft: "12px",
            }}
          >
            Want to make a difference? Share these price disparities with your
            <br />
            Congressman to advocate for fair pricing.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              mt: 2,
            }}
          >
            <ActionButton
              variant="contained"
              sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}
              className="medi-btn"
            >
              Contact Your Congressman
            </ActionButton>
          </Box>
        </Box>

      </Container>
    </div>
  );
}
